import type { ReactNode } from "react";
import { formatCents, formatMesRef } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { Demonstrativo, FolhaItem } from "@/data/folha";

/** Aceita tanto o demonstrativo do colaborador quanto uma linha da folha do RH. */
type Fonte = Demonstrativo | FolhaItem;

interface Props {
  dados: Fonte;
  competencia: string;
  franquiaAmil: number;
  titulo?: string;
  /** Linha abaixo do título; padrão = "Emitir para {tomador}", quando houver. */
  subtitulo?: ReactNode;
  /** Botões à direita do cabeçalho. */
  acoes?: ReactNode;
  className?: string;
}

interface Linha {
  rotulo: string;
  valor: number;
  detalhe?: string;
  desconto?: boolean;
  omitirSeZero?: boolean;
}

/** Negativos com "−" separado, como num extrato: "− R$ 180,00". */
const valorComSinal = (cents: number) => (cents < 0 ? `− ${formatCents(-cents)}` : formatCents(cents));

export const DemonstrativoCard = ({ dados, competencia, franquiaAmil, titulo, subtitulo, acoes, className }: Props) => {
  const linhas: Linha[] = [
    { rotulo: "Salário fixo", valor: dados.salarioFixo, omitirSeZero: true },
    {
      rotulo: "Comissão",
      valor: dados.comissaoBase,
      omitirSeZero: true,
    },
    {
      rotulo: "Bônus de meta",
      valor: dados.bonusMeta,
      omitirSeZero: true,
    },
    {
      // A planilha de origem às vezes traz um Total Geral diferente da soma
      // das partes; é o Total Geral que vale, então mostramos o ajuste em vez
      // de esconder a diferença.
      rotulo: "Ajuste da planilha de comissões",
      valor: dados.comissoes - dados.comissaoBase - dados.bonusMeta,
      omitirSeZero: true,
      detalhe: "Diferença entre o Total Geral informado e a soma de comissão e bônus",
    },
    {
      rotulo: "Coparticipação Amil — titular",
      valor: -dados.amilTitularRepasse,
      desconto: true,
      omitirSeZero: dados.amilTitularBruto === 0,
      detalhe:
        dados.amilTitularBruto > 0
          ? `${formatCents(dados.amilTitularBruto)} em despesas, menos ${formatCents(
              dados.amilFranquiaAplicada,
            )} cobertos pela empresa (franquia de ${formatCents(franquiaAmil)})`
          : undefined,
    },
    // Dependente vira duas linhas porque são coisas diferentes para quem lê: a
    // mensalidade é o custo fixo do plano, que se repete todo mês; a
    // coparticipação é o uso do mês. Nenhuma das duas sofre franquia.
    {
      rotulo: "Amil — mensalidade de dependentes",
      valor: -dados.amilMensalidadeDep,
      desconto: true,
      omitirSeZero: true,
      detalhe: "Mensalidade do plano dos dependentes, repassada integralmente",
    },
    {
      rotulo: "Coparticipação Amil — dependentes",
      valor: -dados.amilCoparticipacaoDep,
      desconto: true,
      omitirSeZero: true,
      detalhe: "Uso do plano por dependentes — repassado integralmente, sem franquia",
    },
    {
      rotulo: "Empréstimos e descontos",
      valor: -dados.emprestimoParcela,
      desconto: true,
      omitirSeZero: true,
    },
    { rotulo: "Outros descontos", valor: -dados.outrosDescontos, desconto: true, omitirSeZero: true },
    {
      rotulo: "Ajuste manual",
      valor: dados.ajusteManual,
      omitirSeZero: true,
      detalhe: dados.ajusteMotivo ?? undefined,
    },
  ].filter((l) => !(l.omitirSeZero && l.valor === 0));

  const sub =
    subtitulo ?? ("tomadorNome" in dados && dados.tomadorNome ? `Emitir para ${dados.tomadorNome}` : undefined);

  return (
    <section className={cn("min-w-0 overflow-hidden rounded-xl border bg-card tabular-nums", className)}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-[18px] sm:px-[22px]">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h3 className="text-base font-semibold first-letter:uppercase">
            {titulo ?? `Demonstrativo — ${formatMesRef(competencia)}`}
          </h3>
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
        {acoes && <div className="flex flex-wrap items-center gap-2">{acoes}</div>}
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_auto] bg-muted-soft text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
        <span className="px-4 py-[9px] sm:pl-[22px]">Composição</span>
        <span className="px-4 py-[9px] text-right sm:pr-[22px]">Valor</span>
      </div>
      {linhas.map((l) => (
        <div
          key={l.rotulo}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 border-t border-border-soft text-sm"
        >
          <div className="min-w-0 px-4 py-[11px] sm:pl-[22px]">
            <p>{l.rotulo}</p>
            {l.detalhe && <p className="mt-0.5 text-xs text-muted-foreground">{l.detalhe}</p>}
          </div>
          <span
            className={cn(
              "whitespace-nowrap px-4 py-[11px] text-right sm:pr-[22px]",
              (l.desconto || l.valor < 0) && "text-destructive",
            )}
          >
            {valorComSinal(l.valor)}
          </span>
        </div>
      ))}

      <div className="flex items-baseline justify-between gap-3 border-t bg-primary/[0.06] px-4 py-4 sm:px-[22px]">
        <span className="text-[15px] font-semibold">Valor da nota fiscal</span>
        <span
          className={cn(
            "text-2xl font-semibold",
            dados.liquido <= 0 ? "text-destructive" : "text-primary-text",
          )}
        >
          {formatCents(dados.liquido)}
        </span>
      </div>
    </section>
  );
};
